import React, { createContext, useContext, useState } from "react";
import { ResourceDWR } from "../dto/ResourceDWR";
import { CreateReviewResponse } from "../components/ReviewService";
import { ReviewDialog } from "../components/rate-and-review/ReviewDialog";
import { useAccount } from "../components/Account";
import { LoginClientManager } from "@tlc/ls2pac/app/ClientManagers/LoginClientManager";
import { ResourceView } from "../dto/ResourceView";

type ShowReviewDialogFn = (
  resource: ResourceDWR | ResourceView,
  rating?: number
) => Promise<CreateReviewResponse | null>;

const ReviewDialogContext = createContext<
  { showReviewDialog: ShowReviewDialogFn } | undefined
>(undefined);

export const ReviewDialogProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated } = useAccount();
  const [resource, setResource] = useState<ResourceDWR | ResourceView | null>(
    null
  );
  const [defaultRating, setDefaultRating] = useState<number>(null);
  const [resolver, setResolver] = useState<
    (result: CreateReviewResponse | null) => void
  >(() => () => {});
  const [open, setOpen] = useState(false);

  const showReviewDialog = async (
    resource: ResourceDWR | ResourceView,
    rating: number = null
  ) => {
    if (!isAuthenticated) {
      const loginSuccess = await new Promise<boolean>((resolve) =>
        LoginClientManager.promptIfNotLoggedIn(
          () => resolve(true),
          () => resolve(false)
        )
      );

      if (!loginSuccess) return null;
    }
    setResource(resource);
    setOpen(true);
    setDefaultRating(rating);
    return new Promise<CreateReviewResponse | null>((resolve) => {
      setResolver(() => resolve);
    });
  };

  const handleClose = (result: CreateReviewResponse | null) => {
    setOpen(false);
    setResource(null);
    resolver(result);
  };

  return (
    <ReviewDialogContext.Provider value={{ showReviewDialog }}>
      {children}
      {resource && (
        <ReviewDialog
          resource={resource}
          defaultRating={defaultRating}
          open={open}
          onClose={handleClose}
        />
      )}
    </ReviewDialogContext.Provider>
  );
};

export const useReviewDialog = () => {
  const context = useContext(ReviewDialogContext);
  if (!context)
    throw new Error("useReviewDialog must be used within ReviewDialogProvider");
  return context;
};
